import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  matchPath,
} from "react-router-dom";


import Home               from "./pages/Home";
import Destinations       from "./pages/Destinations";
import DealDetails        from "./pages/DealDetails";
import Booking            from "./pages/Booking";
import BookingPage        from "./pages/BookingPage";
import Privacy            from "./pages/Privacy";
import Terms              from "./pages/Terms";
import AboutUs            from "./pages/AboutUs";
import About              from "./pages/About";
import Contact            from "./pages/Contact";
import Login              from "./pages/Login";
import Register           from "./pages/Register";
import MyTrips            from "./pages/MyTrips";
import HolidayDetails     from "./pages/HolidayDetails";
import FlightHotelSearch  from "./pages/FlightHotelSearch";
import FlightPriceChecker from "./pages/FlightPriceChecker";
import FlightResults      from "./pages/FlightResults";
import MyBooking from "./pages/MyBooking";
import ReviewsPage from "./pages/ReviewsPage";
import "leaflet/dist/leaflet.css";

import HotelResults         from "./components/HotelResults";
import HotelDetails         from "./pages/HotelDetails";
import Checkout             from "./pages/Checkout";

import PaymentLinkGenerator from "./pages/PaymentLinkGenerator";
import RefundPolicy         from "./pages/RefundPolicy";
import TestAPI              from "./pages/TestAPI";
import CookieBanner         from "./components/CookieBanner";

import ScrollToTop from "./ScrollToTop";
import "./StickyCallBar.css";

/**
 * Single source of truth for page titles.
 * Each entry's `path` uses the exact same syntax as the <Route path="..."> below,
 * so titles can never drift out of sync with routing. Order matters only in that
 * more specific paths should come before less specific ones (matchPath already
 * requires an exact segment match, so "/hotels" and "/hotel/:hotelId" cannot collide).
 */
const PAGE_TITLES = [
  { path: "/",                     name: "Home" },
  { path: "/destinations",         name: "Destinations" },
  { path: "/reviews",              name: "Customer Reviews" },
  { path: "/deal-details/:id",     name: "Deal Details" },
  { path: "/deals/:id",            name: "Deal Details" },
  { path: "/booking/:id",          name: "Booking" },
  { path: "/booking-page",         name: "Booking" },
  { path: "/hotels",               name: "Hotel Search" },
  { path: "/hotel/:hotelId",       name: "Hotel Details" },
  { path: "/flights",              name: "Flight Search" },
  { path: "/flight-results",       name: "Flight Results" },
  { path: "/flight-price-checker", name: "Flight Price Checker" },
  { path: "/holiday/:id",          name: "Holiday Details" },
  { path: "/checkout",             name: "Checkout" },
  { path: "/login",                name: "Login" },
  { path: "/register",             name: "Register" },
  { path: "/my-trips",             name: "My Trips" },
  { path: "/my-booking",           name: "My Booking" },
  { path: "/privacy",              name: "Privacy Policy" },
  { path: "/terms",                name: "Terms & Conditions" },
  { path: "/refund-policy",        name: "Refund Policy" },
  { path: "/about",                name: "About Us" },
  { path: "/about-us",             name: "About Us" },
  { path: "/contact",              name: "Contact Us" },
  { path: "/admin/payment-links",  name: "Admin – Payment Links" },
  { path: "/test-api",             name: "API Test" },
];

function resolvePageName(pathname) {
  // normalise trailing slashes so "/hotels/" still matches "/hotels"
  const cleanPath =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  const match = PAGE_TITLES.find((entry) =>
    matchPath({ path: entry.path, end: true }, cleanPath)
  );

  if (!match && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[PageTracker] No title mapping for path: ${pathname}`);
  }

  return match ? match.name : "Broadway Holidays";
}

function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    const pageName = resolvePageName(location.pathname);
    document.title = `${pageName} | Broadway Holidays`;

    const params        = new URLSearchParams(window.location.search);
    const utmSource     = params.get("utm_source");
    let   trafficSource = "Direct";

    if (utmSource) {
      trafficSource = utmSource;
    } else if (document.referrer) {
      const referrer = document.referrer.toLowerCase();
      if      (referrer.includes("facebook"))  trafficSource = "Facebook";
      else if (referrer.includes("google"))    trafficSource = "Google";
      else if (referrer.includes("instagram")) trafficSource = "Instagram";
      else if (referrer.includes("bing"))      trafficSource = "Bing";
    }

    if (window.Tawk_API?.addEvent) {
      window.Tawk_API.addEvent("Visited Page", {
        page:   pageName,
        url:    window.location.href,
        source: trafficSource,
      });
    }

    const isBookingPage = location.pathname.startsWith("/booking") || location.pathname.startsWith("/checkout");
    let startTime = null;
    if (isBookingPage) startTime = Date.now();

    return () => {
      if (isBookingPage && startTime) {
        const timeSpentSeconds = Math.floor((Date.now() - startTime) / 1000);
        if (timeSpentSeconds > 15 && window.Tawk_API?.addEvent) {
          window.Tawk_API.addEvent("Booking Abandoned", {
            time_spent_seconds: timeSpentSeconds,
            url:  window.location.href,
            page: pageName,
          });
        }
      }
    };
  }, [location]);

  return null;
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <PageTracker />

      <Routes>
        {/* Main */}
        <Route path="/"                      element={<Home />} />
        <Route path="/destinations"          element={<Destinations />} />
        <Route path="/reviews"               element={<ReviewsPage />} />
        <Route path="/deal-details/:id"      element={<DealDetails />} />
        <Route path="/deals/:id"             element={<DealDetails />} />

        {/* Booking */}
        <Route path="/booking/:id"           element={<Booking />} />
        <Route path="/booking-page"          element={<BookingPage />} />

        {/* Hotels */}
        <Route path="/hotels"                element={<HotelResults />} />
        <Route path="/hotel/:hotelId"        element={<HotelDetails />} />

        {/* Flights */}
        <Route path="/flights"               element={<FlightHotelSearch />} />
        <Route path="/flight-results"        element={<FlightResults />} />
        <Route path="/flight-price-checker"  element={<FlightPriceChecker />} />

        {/* Holidays */}
        <Route path="/holiday/:id"           element={<HolidayDetails />} />

        {/* Checkout */}
        <Route path="/checkout"              element={<Checkout />} />

        {/* Account */}
        <Route path="/login"                 element={<Login />} />
        <Route path="/register"              element={<Register />} />
        <Route path="/my-trips"              element={<MyTrips />} />
        <Route path="/my-booking"            element={<MyBooking />} />
        {/* Legal */}
        <Route path="/privacy"               element={<Privacy />} />
        <Route path="/terms"                 element={<Terms />} />
        <Route path="/refund-policy"         element={<RefundPolicy />} />

        {/* Info */}
        <Route path="/about"                 element={<AboutUs />} />
        <Route path="/about-us"              element={<About />} />
        <Route path="/contact"               element={<Contact />} />

        {/* Admin */}
        <Route path="/admin/payment-links"   element={<PaymentLinkGenerator />} />

        {/* Dev/Test */}
        <Route path="/test-api"              element={<TestAPI />} />
      </Routes>

      <div className="sticky-call-bar">
        <div className="call-text"><strong>Call us for live prices &amp; availability</strong></div>
        <a href="tel:020430190055" className="call-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{marginRight:4}}>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l1.91-1.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          020 3019 0055
        </a>
      </div>

      <CookieBanner />

    </Router>
  );
}

export default App;